import { logError } from '@nrdy-marketing-engine/error-handling';
import AccountFormValues from '@nrdy-marketing-engine/forms/@types/account-form-values';
import { useCallback, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';

interface UseValidateEmailParams {
  control: Control<AccountFormValues>;
}

const EMAIL_IN_USE = 'This email address is already in use';
const EMAIL_ERROR = 'An error occurred checking your email';

const userExistsApiPath = `${process.env.NEXT_PUBLIC_VT_API_ENDPOINT}/v1/learn-lab/users/exists`;
const emailAvailable = (email: string) =>
  fetch(`${userExistsApiPath}?email=${encodeURIComponent(email)}`).then((r) => r.status >= 400);

export default function useValidateEmail({ control }: UseValidateEmailParams) {
  const email = useWatch({ control, name: 'email' });
  const [loading, setLoading] = useState(false);

  /**
   * Returns true if the user email is not already taken.
   * Otherwise, it tries to log them in. If all fails, the email field is marked with
   * a validation error and the user may not proceed.
   */
  const validateEmail = useCallback(async () => {
    try {
      setLoading(true);

      // first check if the email is not already taken
      if (await emailAvailable(email)) return true;

      // if sign in fails, mark the email field with an error
      control.setError('email', { message: EMAIL_IN_USE, type: 'custom' });
    } catch (ex) {
      control.setError('email', { message: EMAIL_ERROR, type: 'custom' });
      logError(ex);
    } finally {
      setLoading(false);
    }
    return false;
  }, [control, email]);

  return { loading, validateEmail };
}
